import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Assessment as AssessmentIcon,
  Home as HomeIcon,
  LocalShipping as ShippingIcon,
  Storage as StorageIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';

const Home = () => {
  const features = [
    {
      icon: <AssessmentIcon />,
      title: 'Furniture Assessment',
      description: 'Upload images of your furniture and receive personalized recommendations on what to keep, store, sell, or discard. Get estimated values and condition assessments to make informed decisions.',
    },
    {
      icon: <HomeIcon />,
      title: 'Space Planning',
      description: 'Input dimensions for your rental unit and rebuilt home to receive optimized furniture layouts. Maximize space utilization with intelligent recommendations tailored to your specific needs.',
    },
    {
      icon: <ShippingIcon />,
      title: 'Packing & Moving Services',
      description: 'Browse and book trusted vendors for packing and moving services directly through the platform. Track your bookings in real-time and manage the entire moving process seamlessly.',
    },
    {
      icon: <StorageIcon />,
      title: 'Storage Solutions',
      description: 'Find secure and affordable storage facilities in your city. Manage your stored inventory, track items, and handle storage bookings all from one convenient platform.',
    },
    {
      icon: <PaletteIcon />,
      title: 'Interior Design Recommendations',
      description: 'Get professional design suggestions for your rebuilt home including color schemes, furniture layouts, decor ideas, and lighting recommendations. Plan your dream space with confidence.',
    },
  ];

  const benefits = [
    'Save money by making smart decisions about furniture',
    'Manage your entire relocation from one platform',
    'Connect with verified service providers in your city',
    'Get personalized recommendations based on your needs',
    'Track all services and bookings in real-time',
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            ReNest
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, opacity: 0.9 }}>
            Smart Furniture Management for Building Redevelopment
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 700, mx: 'auto', fontSize: '1.1rem' }}>
            Optimize costs, plan your space, and manage your transition to a rental unit and back. 
            Get personalized recommendations and access trusted services all in one place.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={Link}
            to="/register"
            sx={{ mr: 2, px: 4, py: 1.5 }}
          >
            Get Started Free
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            component={Link}
            to="/login"
            sx={{ px: 4, py: 1.5 }}
          >
            Login
          </Button>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ py: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 4, fontWeight: 600 }}>
          Everything You Need for a Smooth Transition
        </Typography>
        
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          {features.map((feature, index) => (
            <Box key={index} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ color: 'primary.main', mr: 2, mt: 0.5 }}>
                  {feature.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
              {index < features.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
          ))}
        </Box>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 6 }}>
        <Container>
          <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ mb: 4, fontWeight: 600 }}>
            Why Homeowners Choose ReNest
          </Typography>
          <Box sx={{ maxWidth: 700, mx: 'auto' }}>
            <List>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit}
                    primaryTypographyProps={{ 
                      variant: 'body1',
                      sx: { fontSize: '1.05rem' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={Link}
              to="/register"
              sx={{ px: 5, py: 1.5 }}
            >
              Start Your Project Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;

